import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import { debounce } from "lodash";
import { toast } from "react-toastify";

// Services & Types
import { AttendanceService } from "services/attendance.service";
import { IAttendanceResponse } from "models/church.types";

export default function AttendanceList() {
  const [attendances, setAttendances] = useState<IAttendanceResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Récupération des paramètres d'URL
  const location = useLocation();
  const history = useHistory();
  const queryParams = new URLSearchParams(location.search);
  const eventIdFromUrl = queryParams.get("eventId");

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  /**
   * Charge les données avec 3 cas possibles :
   * 1. Filtrage par Event (venant du Dashboard ou d'un raccourci)
   * 2. Recherche par texte via Elasticsearch
   * 3. Liste globale (via recherche vide)
   */
  const loadData = async (query: string, page: number) => {
    setLoading(true);
    try {
      let result;

      if (eventIdFromUrl && !query.trim()) {
        // CAS 1 : On vient du Dashboard avec un Event spécifique
        result = await AttendanceService.getByEvent(
          Number(eventIdFromUrl),
          page,
          itemsPerPage,
        );
      } else {
        // CAS 2 & 3 : Recherche textuelle ou Liste complète (recherche vide)
        result = await AttendanceService.search(query, page, itemsPerPage);
      }

      setAttendances(result.data);
      setTotalItems(result.totalCount);
    } catch (error) {
      console.error("Erreur chargement présences:", error);
      toast.error("Impossible de charger le registre des présences");
    } finally {
      setLoading(false);
    }
  };

  // On surveille le changement de page ET le changement de l'ID dans l'URL
  useEffect(() => {
    loadData(searchTerm, currentPage);
  }, [currentPage, eventIdFromUrl]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setCurrentPage(0);
      loadData(value, 0);
    }, 500),
    [eventIdFromUrl],
  );

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleResetFilter = () => {
    setSearchTerm("");
    setCurrentPage(0);
    history.push("/admin/attendances"); // Nettoie l'URL
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Supprimer cet enregistrement de présence ?")) {
      setLoading(true);
      try {
        await AttendanceService.delete(id);
        toast.success("Enregistrement de présence supprimé");
        loadData(searchTerm, currentPage);
      } catch (error) {
        console.error("Erreur suppression présence:", error);
        toast.error("Erreur lors de la suppression.");
      } finally {
        setLoading(false);
      }
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <div className="flex flex-col w-full px-6 min-h-screen">
      {/* TITRE PRINCIPAL */}
      <div className="mb-6 pt-8">
        <h2 className="text-white text-3xl font-bold uppercase tracking-tight">
          Registre des Présences
        </h2>
        <p className="text-slate-400 text-sm italic">
          {eventIdFromUrl
            ? `Filtrage par événement #${eventIdFromUrl}`
            : "Suivi de la participation des membres aux différents événements"}
        </p>
      </div>

      {/* BARRE D'OUTILS */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-2 w-full md:w-auto">
          <div className="relative w-full md:w-96 group">
            <span className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-[#D4AF37] transition-colors">
              {loading ? (
                <i className="fas fa-circle-notch animate-spin text-[#D4AF37]"></i>
              ) : (
                <i className="fas fa-search"></i>
              )}
            </span>
            <input
              type="text"
              value={searchTerm}
              onChange={onSearchChange}
              placeholder="Rechercher un membre ou un événement..."
              className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg pl-11 pr-4 py-2 font-sans h-[38px] outline-none focus:border-blue-500 transition-colors"
            />
          </div>

          {/* Bouton de réinitialisation si filtre actif */}
          {eventIdFromUrl && (
            <button
              type="button"
              onClick={handleResetFilter}
              className="bg-slate-700 text-white p-3.5 rounded-xl hover:bg-red-500/20 hover:text-red-500 transition-all border border-slate-600"
              title="Effacer le filtre événement"
            >
              <i className="fas fa-times-circle"></i>
            </button>
          )}
        </div>

        <Link
          to="/admin/attendances/register"
          className="bg-[#D4AF37] hover:bg-[#b8952e] text-slate-900 font-bold px-8 h-[38px] flex items-center justify-center rounded-lg whitespace-nowrap transition-colors w-full lg:w-auto text-center text-sm"
        >
          <i className="fas fa-user-check mr-2 text-base"></i> Marquer une
          présence
        </Link>
      </div>

      {/* TABLE CARD */}
      <div className="relative flex flex-col min-w-0 break-words w-full mb-10 shadow-2xl rounded-2xl bg-slate-800 border border-slate-700 overflow-hidden">
        <div className="block w-full overflow-x-auto">
          <table className="items-center w-full bg-transparent border-collapse text-left">
            <thead>
              <tr className="bg-[#1E293B] text-slate-300 uppercase text-[10px] font-black tracking-widest border-b border-slate-700">
                <th className="px-6 py-5">Membre</th>
                <th className="px-6 py-5">Événement</th>
                <th className="px-6 py-5 text-center">Statut</th>
                <th className="px-6 py-5 text-center">Date de marquage</th>
                <th className="px-6 py-5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50 text-slate-300">
              {attendances.length === 0 && !loading ? (
                <tr>
                  <td
                    colSpan={5}
                    className="text-center py-16 text-slate-500 italic text-sm"
                  >
                    <i className="fas fa-user-slash block text-3xl mb-3 opacity-20"></i>
                    Aucune présence trouvée pour ce critère.
                  </td>
                </tr>
              ) : (
                attendances.map((att) => (
                  <tr
                    key={att.id}
                    className="hover:bg-slate-700/40 transition-colors group"
                  >
                    {/* Membre */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center text-[#D4AF37] font-bold mr-4 border border-slate-700">
                          {att.memberLastName?.charAt(0) || "M"}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-bold text-white uppercase tracking-tight">
                            {att.memberLastName || "Membre Inconnu"}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* Événement */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col max-w-xs">
                        <span className="text-xs font-bold text-slate-200 uppercase tracking-tight line-clamp-1">
                          {att.eventTitle || "Événement non spécifié"}
                        </span>
                        {att.remarks && (
                          <span className="text-[11px] text-slate-400 italic line-clamp-1 mt-0.5">
                            Note : {att.remarks}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Statut (Badge Présent / Absent) */}
                    <td className="px-6 py-4 text-center whitespace-nowrap">
                      {att.present ? (
                        <span className="inline-flex items-center bg-green-500/10 text-green-400 text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full border border-green-500/20">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-400 mr-1.5 animate-pulse"></span>
                          Présent
                        </span>
                      ) : (
                        <span className="inline-flex items-center bg-red-500/10 text-red-400 text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full border border-red-500/20">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-400 mr-1.5"></span>
                          Absent
                        </span>
                      )}
                    </td>

                    {/* Date de marquage */}
                    <td className="px-6 py-4 text-[10px] font-medium text-center uppercase whitespace-nowrap">
                      <span className="bg-slate-900/40 px-2.5 py-1.5 rounded text-slate-400 border border-slate-700/40">
                        {att.createdDate
                          ? new Date(att.createdDate).toLocaleString("fr-FR", {
                              dateStyle: "short",
                              timeStyle: "short",
                            })
                          : "N/A"}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4 text-right global-actions">
                      <div className="flex justify-end gap-5">
                        <Link
                          to={`/admin/attendances/edit/${att.id}`}
                          className="text-slate-400 hover:text-[#D4AF37] transition-colors"
                          title="Modifier"
                        >
                          <i className="fas fa-edit text-lg"></i>
                        </Link>
                        <button
                          onClick={() => handleDelete(att.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                          title="Supprimer"
                        >
                          <i className="fas fa-trash-alt text-lg"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="px-6 py-5 bg-[#0F172A]/50 border-t border-slate-700 flex items-center justify-between">
          <p className="text-[11px] text-slate-500 font-bold uppercase tracking-wider">
            Total : <span className="text-[#D4AF37]">{totalItems}</span>{" "}
            enregistrements
          </p>
          <div className="flex gap-2">
            <button
              disabled={currentPage === 0}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Précédent
            </button>
            <button
              disabled={currentPage + 1 >= totalPages}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Suivant
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
